import SentryBreadcrumb, { BreadcrumbCategory } from "./SentryBreadcrumb";
import ErrorLevel from "./ErrorLevel";

/**
 * Коллекция breadcrumbs (хлебных крошек)
 * Хранит последовательность действий пользователя для контекста ошибки
 */
class SentryBreadcrumbList {

  private static items: SentryBreadcrumb[] = [];
  private static readonly MAX_SIZE = 50;

  /**
   * Добавить breadcrumb в список
   * Breadcrumbs — это след действий пользователя перед ошибкой:
   * клики, навигация, API вызовы и т.д.
   */
  static add(breadcrumb: SentryBreadcrumb): void {
    this.items.push(breadcrumb);
    if (this.items.length > this.MAX_SIZE) {
      this.items.shift();
    }
  }

  /**
   * Добавить breadcrumb навигации (переход между страницами)
   */
  static addNavigation(to: any, from: any): void {
    this.add(SentryBreadcrumb.fromNavigation(to, from));
  }

  /**
   * Добавить breadcrumb диалога
   */
  static addDialog(dialogName: string, action: 'open' | 'close'): void {
    this.add(SentryBreadcrumb.fromDialog(dialogName, action));
  }

  /**
   * Добавить произвольный breadcrumb
   * Используется для ручной разметки важных действий в коде
   */
  static addCustom(
    message: string,
    category: BreadcrumbCategory,
    level: ErrorLevel,
    data: Record<string, any> = {}
  ): void {
    this.add(SentryBreadcrumb.create(message, category, level, data));
  }

  /**
   * Добавить серверные breadcrumbs из RPC ответа (meta.debug.breadcrumbs)
   * Сохраняет оригинальные timestamp'ы с бэкенда
   */
  static addServerBreadcrumbs(breadcrumbs?: any[]): void {
    if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) return;

    for (const crumb of breadcrumbs) {
      this.add(SentryBreadcrumb.fromServer(crumb));
    }
  }

  /**
   * Добавить trace-крошку для отладки.
   *
   * Крошки накапливаются в циклическом буфере и прикрепляются к следующей ошибке,
   * отправленной в Sentry. Позволяют восстановить цепочку действий перед сбоем.
   *
   * Принимает три формата вызова:
   * - только сообщение
   * - сообщение + данные
   * - только данные (без сообщения)
   *
   * @param messageOrPayload — текст крошки или объект с данными
   * @param payload          — данные, если первый аргумент — строка
   *
   * @example
   * // Только сообщение
   * SentryService.trace('Открыли карточку ЗТК');
   *
   * @example
   * // Сообщение с данными
   * SentryService.trace('Загрузили позиции', { count: 15, documentClass: 'Assembly' });
   *
   * @example
   * // Только данные (без сообщения)
   * SentryService.trace({ rate: 87.5, currency: 'USD' });
   *
   * @example
   * // В потоке работы — крошки покажут что было перед ошибкой
   * SentryService.trace('Пользователь нажал "Сохранить"');
   * SentryService.trace('Отправляем transportOrders.update', { id: 42 });
   * // ... если update упадёт — обе крошки прикрепятся к ошибке
   */
  static trace(messageOrPayload?: string | any, payload?: any): void {
    let message = '';
    let data = {};

    if (typeof messageOrPayload === 'string') {
      message = messageOrPayload;
      if (payload !== undefined) data = { payload };
    } else if (messageOrPayload !== undefined) {
      data = { payload: messageOrPayload };
    }

    this.add(SentryBreadcrumb.create(message, BreadcrumbCategory.Debug, ErrorLevel.Debug, data));
  }

  /**
   * Добавить warn-крошку с уровнем Warning
   */
  static traceWarn(message: string, payload?: any): void {
    const data = payload !== undefined ? { payload } : {};
    this.add(SentryBreadcrumb.create(message, BreadcrumbCategory.Debug, ErrorLevel.Warning, data));
  }

  static getAll(): SentryBreadcrumb[] {
    return [...this.items];
  }

  static clear(): void {
    this.items = [];
  }
}

export default SentryBreadcrumbList;
